import * as pdfjsLib from 'pdfjs-dist';
import { RegionBox } from '../models/region-box.model';
import { Const } from '../static/const';
import { Size } from '../models/size.model';

export class Converters {
  static dataURItoBlob(dataURI: any): Blob {
    const binary = atob(dataURI.split(',')[1]);
    const array: any = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
  }

  static async pdfToImages(file: File): Promise<string[]> {
    return new Promise<string[]>((resolve) => {
      const images: string[] = [];
      const reader: any = new FileReader();
      reader.onload = async () => {
        const scale = 1;
        const canvas = document.createElement('canvas');
        try {
          const pdf = await pdfjsLib.getDocument({ data: reader.result })
            .promise;
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale });
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            const renderContext: any = {
              canvasContext: context,
              viewport,
            };

            await page.render(renderContext).promise;
            const image = canvas.toDataURL('image/jpeg', 1);
            images.push(image);
          }
        } catch (reason) {
          console.log(reason);
        }
        resolve(images);
      };
      reader.onerror = () => {
        resolve(images);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  static cropImage(image: HTMLImageElement, regionBox: RegionBox): string {
    const sx = image.width * regionBox.leftTopX;
    const sy = image.height * regionBox.leftTopY;
    const sw = image.width * regionBox.rightBottomX - sx;
    const sh = image.height * regionBox.rightBottomY - sy;
    const dw = sw > Const.minRegionSizePx ? sw : Const.minRegionSizePx;
    const dh = sh > Const.minRegionSizePx ? sh : Const.minRegionSizePx;

    const canvas = document.createElement('canvas');
    const context: any = canvas.getContext('2d');
    canvas.width = dw;
    canvas.height = dh;
    context.drawImage(image, sx, sy, sw, sh, 0, 0, dw, dh);
    return canvas.toDataURL('image/jpeg', 1);
  }

  static cropImages(image: string, regions: RegionBox[]): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      const imageEl = new Image();
      imageEl.onload = () => {
        const croppedImages: string[] = [];
        regions.forEach((region) => {
          croppedImages.push(Converters.cropImage(imageEl, region));
        });
        resolve(croppedImages);
      };
      imageEl.onerror = () => {
        reject();
      };
      imageEl.src = image;
    });
  }

  static getPngDimensions(base64: string): Promise<Size> {
    return new Promise<Size>((resolve, reject) => {
      const imageEl = new Image();
      imageEl.onload = () => {
        resolve({
          width: imageEl.width,
          height: imageEl.height,
        });
      };
      imageEl.onerror = () => {
        reject();
      };
      imageEl.src = base64;
    });
  }

  //function to convert excel file into base64
  static toBase64(file: any): Promise<string> {
    return new Promise((resolve, reject) => {
      // Add validation to ensure file is a File or Blob
      if (!file || (!(file instanceof File) && !(file instanceof Blob))) {
        const errorMsg = `Expected File or Blob, got ${typeof file} with constructor ${file?.constructor?.name || 'unknown'}`;
        console.error('toBase64 error:', errorMsg);
        reject(new Error(errorMsg));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  //to convert base64 to pdf
  static base64toPDF(data, format, fileName?) {
    var bufferArray = this.base64ToArrayBuffer(data);
    let type;

    if (format == 'png') {
      type = 'image/png';
    } else if (
      format == 'pdf' ||
      format ==
        'vnd.openxmlformats-officedocument.presentationml.presentation' ||
      format == 'vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      type = 'application/pdf';
    } else if (format == 'jpeg') {
      type = 'image/jpeg';
    } else if (format == 'jpg') {
      type = 'image/jpg';
    }

    var blobStore = new Blob([bufferArray], { type: type });

    return blobStore;
  }

  //to convert base64 to buffer array
  static base64ToArrayBuffer(data) {
    var bString = window.atob(data);
    var bLength = bString.length;
    var bytes = new Uint8Array(bLength);
    for (var i = 0; i < bLength; i++) {
      var ascii = bString.charCodeAt(i);
      bytes[i] = ascii;
    }
    return bytes;
  }

  static base64ToFile(base64String, mimeType, filename) {
    // Remove data URL scheme if present
    const byteCharacters = atob(base64String); // Decode Base64 string
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    // Create a File object
    const file = new File([blob], filename, { type: mimeType });

    return file;
  }

  //sanitize the object to remove functions and symbols
  // static sanitizeObject(obj: any, seen = new WeakSet()): any {
  //   if (obj === null || typeof obj !== 'object') return obj;
  //   if (seen.has(obj)) return undefined;

  //   seen.add(obj);

  //   const cleanObj: any = Array.isArray(obj) ? [] : {};
  //   for (const key in obj) {
  //     if (Object.prototype.hasOwnProperty.call(obj, key)) {
  //       const value = obj[key];
  //       if (typeof value !== 'function' && typeof value !== 'symbol') {
  //         cleanObj[key] = this.sanitizeObject(value, seen);
  //       }
  //     }
  //   }
  //   return cleanObj;
  // }

  static sanitizeObject(obj: any, seen = new WeakSet()): any {
    if (obj === null || typeof obj !== 'object') return obj;
    if (seen.has(obj)) return obj; // Don't return undefined, just return the object

    seen.add(obj);

    if (Array.isArray(obj)) {
      return obj.map((item) => this.sanitizeObject(item, seen));
    }

    const cleanObj: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (typeof value !== 'function' && typeof value !== 'symbol') {
          cleanObj[key] = this.sanitizeObject(value, seen);
        }
      }
    }
    return cleanObj;
  }

  static arrayChecker(value) {
    if (Array.isArray(value)) {
      // Case: manual_risk is ['NA']
      return value[0];
    } else if ('prediction' in value && Array.isArray(value.prediction)) {
      // Case: manual_risk is { prediction: ['NA'] }
      return value.prediction[0];
    } else {
      // Fallback in case of unexpected format
      return [];
    }
  }
}
